import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Download, Loader2, Lock, LogOut, RefreshCw, Search, Send, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LEAD_STATUSES, STATUS_LABELS } from "@/lib/services";

const STATUS_STYLE = {
    new: "border-gold/50 text-gold",
    contacted: "border-sky-400/40 text-sky-300",
    estimate_requested: "border-violet-400/40 text-violet-300",
    quoted: "border-cyan-400/40 text-cyan-300",
    won: "border-emerald-400/40 text-emerald-300",
    lost: "border-slate-500/40 text-slate-400",
};

const KIND_LABEL = { builder_outreach: "AI builder outreach", customer_reply: "Customer auto-reply", owner_notification: "Owner notification", weekly_digest: "Weekly digest" };

function Login({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            onLogin(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mx-auto max-w-md px-4 pb-24 pt-40">
            <form onSubmit={submit} data-testid="admin-login-form" className="border border-gold/25 bg-ink-3 p-10">
                <Lock size={28} className="text-gold" />
                <h1 className="font-display mt-5 text-2xl font-bold">Owner login</h1>
                <p className="mt-2 text-sm text-slate-400">Lead dashboard access — owner only.</p>
                <div className="mt-8 space-y-5">
                    <div>
                        <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400" htmlFor="admin-email">Email</label>
                        <input id="admin-email" data-testid="admin-login-email" className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
                    </div>
                    <div>
                        <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400" htmlFor="admin-password">Password</label>
                        <input id="admin-password" data-testid="admin-login-password" className="field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                    </div>
                </div>
                <button type="submit" disabled={loading} data-testid="admin-login-submit"
                    className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-gold px-6 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                    {loading ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />}
                    {loading ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </div>
    );
}

function LeadDrawer({ lead, onClose, onSave }) {
    const [status, setStatus] = useState(lead.status);
    const [notes, setNotes] = useState(lead.notes || "");
    const [nextAction, setNextAction] = useState(lead.next_action || "");
    const [saving, setSaving] = useState(false);

    const viewAttachment = async (a) => {
        try {
            const res = await api.get(`/admin/attachments/${a.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            toast.error("Could not open attachment.");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/leads/${lead.lead_ref}`, { status, notes, next_action: nextAction });
            onSave(data);
            toast.success(`Lead ${lead.lead_ref} updated.`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end bg-ink/80 backdrop-blur-sm" onClick={onClose} data-testid="admin-lead-drawer">
            <div className="h-full w-full max-w-lg overflow-y-auto border-l border-gold/25 bg-ink-2 p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="font-mono text-xs text-gold">{lead.lead_ref}</p>
                        <h3 className="font-display mt-2 text-2xl font-bold">{lead.name}</h3>
                        <p className="mt-1 font-mono text-xs text-slate-400">{new Date(lead.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={onClose} data-testid="admin-lead-drawer-close" aria-label="Close" className="border border-white/20 p-2.5 text-slate-300 hover:border-gold hover:text-gold">✕</button>
                </div>
                <dl className="mt-8 space-y-3 text-sm">
                    {[
                        ["Email", lead.email], ["Phone", lead.phone], ["Address / city", lead.address || lead.region],
                        ["Property type", lead.property_type], ["Project size", lead.project_size],
                        ["Preferred start", lead.start_date], ["Source", lead.source === "contractor" ? "Contractor inquiry" : "Customer quote"],
                        ["Services", (lead.services || []).join(", ") || lead.project_type],
                    ].map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-6 border-b border-white/5 pb-2">
                            <dt className="shrink-0 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{k}</dt>
                            <dd className="text-right break-words text-slate-200">{v || "—"}</dd>
                        </div>
                    ))}
                </dl>
                {lead.message && (
                    <div className="mt-6">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Description</p>
                        <p className="mt-2 text-sm leading-relaxed text-slate-300">{lead.message}</p>
                    </div>
                )}
                {(lead.attachments || []).length > 0 && (
                    <div className="mt-6" data-testid="admin-lead-attachments">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Attachments</p>
                        <ul className="mt-3 space-y-2">
                            {lead.attachments.map((a, i) => (
                                <li key={a.id}>
                                    <button onClick={() => viewAttachment(a)} data-testid={`admin-attachment-view-${i}`}
                                        className="flex w-full items-center justify-between gap-3 border border-gold/25 bg-ink-3 px-4 py-3 text-left text-xs text-slate-200 transition-colors hover:border-gold hover:text-gold">
                                        <span className="truncate">{a.name}</span>
                                        <span className="shrink-0 font-mono text-[10px] text-slate-500">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="mt-8 space-y-5 border-t border-gold/15 pt-8">
                    <div>
                        <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400" htmlFor="lead-status">Status</label>
                        <select id="lead-status" data-testid="admin-lead-status-select" className="field" value={status} onChange={(e) => setStatus(e.target.value)}>
                            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400" htmlFor="lead-next">Next action date</label>
                        <input id="lead-next" data-testid="admin-lead-next-action" className="field" type="date" value={nextAction} onChange={(e) => setNextAction(e.target.value)} />
                    </div>
                    <div>
                        <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400" htmlFor="lead-notes">Private notes</label>
                        <textarea id="lead-notes" data-testid="admin-lead-notes" className="field min-h-24" placeholder="Only you can see this…" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                    <button onClick={save} disabled={saving} data-testid="admin-lead-save"
                        className="inline-flex w-full items-center justify-center gap-2 bg-gold px-6 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                        {saving ? <Loader2 size={15} className="animate-spin" /> : null} Save changes
                    </button>
                </div>
            </div>
        </div>
    );
}

function Dashboard({ user, onLogout }) {
    const [leads, setLeads] = useState(null);
    const [stats, setStats] = useState(null);
    const [outreach, setOutreach] = useState([]);
    const [builders, setBuilders] = useState([]);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [tab, setTab] = useState("leads");
    const [active, setActive] = useState(null);
    const [newBuilder, setNewBuilder] = useState({ name: "", email: "" });

    const load = useCallback(async () => {
        try {
            const [l, s, o, b] = await Promise.all([
                api.get("/admin/leads", { params: { status: status || undefined, q: q || undefined } }),
                api.get("/admin/stats"), api.get("/admin/outreach"), api.get("/admin/builders"),
            ]);
            setLeads(l.data); setStats(s.data); setOutreach(o.data); setBuilders(b.data);
        } catch (err) {
            if (err?.response?.status === 401) onLogout();
            else toast.error("Could not load dashboard data.");
        }
    }, [status, q, onLogout]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = async () => {
        try {
            const res = await api.get("/admin/leads/export", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("CSV exported.");
        } catch {
            toast.error("Export failed.");
        }
    };

    const sendDigest = async () => {
        try {
            const { data } = await api.post("/admin/digest/send");
            data.sent ? toast.success(`Digest sent — "${data.subject}"`) : toast.info(data.reason || "Digest not sent");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    const addBuilder = async (e) => {
        e.preventDefault();
        if (!/^\S+@\S+\.\S+$/.test(newBuilder.email)) return toast.error("Enter a valid builder email.");
        try {
            await api.post("/builders", newBuilder);
            toast.success("Builder partner added.");
            setNewBuilder({ name: "", email: "" });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-8 lg:px-16 lg:pt-36" data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Lead dashboard</p>
                    <h1 className="font-display mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">Welcome{user?.name ? `, ${user.name}` : ""}.</h1>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={exportCsv} data-testid="admin-export-csv" className="inline-flex items-center gap-2 border border-gold/40 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-ink">
                        <Download size={14} /> Export CSV
                    </button>
                    <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-slate-300 hover:border-red-400 hover:text-red-300">
                        <LogOut size={14} /> Log out
                    </button>
                </div>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {stats ? [
                    ["Total leads", stats.total_leads, "admin-stat-total"],
                    ["New", stats.by_status.new, "admin-stat-new"],
                    ["Quoted", stats.by_status.quoted, "admin-stat-quoted"],
                    ["Won", stats.by_status.won, "admin-stat-won"],
                    ["Emails sent", stats.emails_sent, "admin-stat-emails"],
                ].map(([label, value, id]) => (
                    <div key={id} data-testid={id} className="border border-gold/15 bg-ink-3 p-6">
                        <span className="font-display block text-3xl font-extrabold text-gold">{value}</span>
                        <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
                    </div>
                )) : <p className="text-sm text-slate-400">Loading…</p>}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3 border-b border-gold/15 pb-4">
                {[["leads", "Leads"], ["outreach", "AI Outreach"], ["builders", "Builder Partners"]].map(([id, label]) => (
                    <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
                        className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.18em] transition-colors ${tab === id ? "bg-gold text-ink" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === "leads" && (
                <div className="mt-8">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-56">
                            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input data-testid="admin-search" className="field pl-10" placeholder="Search name, email, phone, city…" value={q} onChange={(e) => setQ(e.target.value)} />
                        </div>
                        <select data-testid="admin-status-filter" className="field w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">All statuses</option>
                            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                        <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 border border-white/15 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 hover:border-gold hover:text-gold">
                            <RefreshCw size={13} /> Refresh
                        </button>
                    </div>
                    <div className="mt-6 space-y-3" data-testid="admin-leads-table">
                        {leads === null && <p className="text-sm text-slate-400">Loading…</p>}
                        {leads && leads.length === 0 && (
                            <p className="border border-dashed border-gold/30 p-8 text-center text-sm text-slate-500">
                                No leads yet — quote requests from the website appear here instantly.
                            </p>
                        )}
                        {leads && leads.map((l, i) => (
                            <button key={l.lead_ref} data-testid={`admin-lead-row-${i}`} onClick={() => setActive(l)}
                                className="flex w-full flex-wrap items-center justify-between gap-4 border border-gold/15 bg-ink-3 p-5 text-left transition-colors hover:border-gold/50">
                                <div className="min-w-0">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{l.lead_ref} · {l.source === "contractor" ? "Contractor" : "Customer"}</p>
                                    <p className="mt-1 truncate text-sm font-semibold text-slate-100">{l.name} — {(l.services || []).join(", ") || l.project_type || "Painting"}</p>
                                    <p className="mt-0.5 truncate text-xs text-slate-500">{l.email} · {l.phone} · {l.address || l.region || "GTA"}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-[10px] text-slate-500">{new Date(l.created_at).toLocaleDateString()}</span>
                                    <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLE[l.status] || ""}`}>{STATUS_LABELS[l.status]}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {tab === "outreach" && (
                <div className="mt-8 space-y-4" data-testid="admin-outreach-log">
                    <div className="flex flex-wrap items-center justify-between gap-4 border border-gold/25 bg-gold/5 p-6">
                        <div>
                            <h3 className="font-display flex items-center gap-2 text-lg font-bold"><Sparkles size={16} className="text-gold" /> AI builder outreach & weekly digest</h3>
                            <p className="mt-1 text-xs text-slate-400">Auto-replies to customers, AI-drafted partner emails on every new quote, digest every 7 days.</p>
                        </div>
                        <button onClick={sendDigest} data-testid="admin-digest-send" className="inline-flex items-center gap-2 bg-gold px-5 py-3 font-display text-xs font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5">
                            <Send size={13} /> Send digest now
                        </button>
                    </div>
                    {outreach.length === 0 && <p className="border border-dashed border-gold/30 p-8 text-center text-sm text-slate-500">No email activity yet.</p>}
                    {outreach.map((e, i) => (
                        <div key={i} data-testid={`admin-outreach-row-${i}`} className="flex items-start justify-between gap-6 border border-gold/15 bg-ink-3 p-5">
                            <div className="min-w-0">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{KIND_LABEL[e.kind] || e.kind}{e.lead_ref ? ` · ${e.lead_ref}` : ""}</p>
                                <p className="mt-1.5 truncate text-sm font-semibold text-slate-100">{e.subject || "—"}</p>
                                <p className="mt-1 truncate text-xs text-slate-500">to: {e.recipients.join(", ") || "—"}</p>
                            </div>
                            <span className={`shrink-0 border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${e.status === "sent" ? "border-emerald-400/40 text-emerald-300" : e.status === "failed" ? "border-red-400/40 text-red-300" : "border-slate-500/40 text-slate-400"}`}>{e.status}</span>
                        </div>
                    ))}
                </div>
            )}

            {tab === "builders" && (
                <div className="mt-8 grid gap-8 lg:grid-cols-2">
                    <div className="border border-gold/15 bg-ink-3 p-8" data-testid="admin-builders-list">
                        <h3 className="font-display flex items-center gap-2 text-lg font-bold"><Building2 size={17} className="text-gold" /> Builder partners ({builders.length})</h3>
                        <p className="mt-2 text-xs text-slate-500">These emails receive AI outreach on new customer quotes and the weekly digest.</p>
                        <ul className="mt-5 space-y-3 text-sm">
                            {builders.map((b, i) => (
                                <li key={i} data-testid={`admin-builder-${i}`} className="flex items-center gap-3 border border-white/5 bg-ink p-4">
                                    <Building2 size={14} className="text-gold" /> <span className="text-slate-200">{b.email}</span> <span className="ml-auto font-mono text-[10px] text-slate-500">{b.name}</span>
                                </li>
                            ))}
                            {builders.length === 0 && <li className="text-sm text-slate-500">No partners yet.</li>}
                        </ul>
                    </div>
                    <form onSubmit={addBuilder} className="border border-gold/15 bg-ink-3 p-8" data-testid="admin-add-builder-form">
                        <h3 className="font-display text-lg font-bold">Add builder partner</h3>
                        <div className="mt-5 space-y-4">
                            <input data-testid="admin-builder-name" className="field" placeholder="Builder name (e.g. Acme Homes)" value={newBuilder.name} onChange={(e) => setNewBuilder((v) => ({ ...v, name: e.target.value }))} aria-label="Builder name" />
                            <input data-testid="admin-builder-email" className="field" type="email" placeholder="builder@company.com" value={newBuilder.email} onChange={(e) => setNewBuilder((v) => ({ ...v, email: e.target.value }))} aria-label="Builder email" />
                        </div>
                        <button type="submit" data-testid="admin-builder-add-button" className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-gold px-6 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5">
                            <Building2 size={15} /> Add builder partner
                        </button>
                    </form>
                </div>
            )}

            {active && <LeadDrawer lead={active} onClose={() => setActive(null)} onSave={(d) => { setLeads((ls) => ls.map((l) => (l.lead_ref === d.lead_ref ? d : l))); setActive(null); load(); }} />}
        </div>
    );
}

export default function Admin() {
    const [user, setUser] = useState("checking");

    useEffect(() => {
        api.get("/auth/me")
            .then(({ data }) => setUser(data))
            .catch(() => setUser(null));
    }, []);

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* noop */ }
        setUser(null);
    };

    return (
        <div data-testid="admin-page">
            {user === "checking" ? (
                <div className="flex min-h-[60vh] items-center justify-center"><Loader2 size={26} className="animate-spin text-gold" /></div>
            ) : user ? (
                <Dashboard user={user} onLogout={logout} />
            ) : (
                <Login onLogin={setUser} />
            )}
        </div>
    );
}